import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  selectAuditsEndLogId,
  selectAuditsStartLogId,
  setAuditsStartEndLogIds,
  setSingleLogTime,
} from './redux';
import {
  buildLogIdDateMsMap,
  clearAuditsStartEndLogIdBound,
  nextAuditsStartEndLogIds,
  startEndLogIdsFromClicked,
  timeRangeFromLogIds,
} from './auditsLogIdRange';

/** /audits date-icon start/end — log-id slice by date; also stores the time range. */
export const useAuditsLogIdRange = (
  logs: Array<{ id?: string | null, date?: string | null } | null | undefined> | null,
) => {
  const dispatch = useAppDispatch();
  const startLogId = useAppSelector(selectAuditsStartLogId);
  const endLogId = useAppSelector(selectAuditsEndLogId);
  const dateById = useMemo(() => buildLogIdDateMsMap(logs || []), [logs]);

  const applyIdsAndTime = (next: {
    startLogId?: string | null,
    endLogId?: string | null,
  } | null) => {
    dispatch(setAuditsStartEndLogIds(
      next
        ? {
          startLogId: next.startLogId || null,
          endLogId: next.endLogId || null,
        }
        : null,
    ));
    const range = timeRangeFromLogIds(
      next?.startLogId,
      next?.endLogId,
      dateById,
    );
    if (range) dispatch(setSingleLogTime(range));
  };

  const applyLogIdRangeBound = (which: 'start' | 'end', sourceIds: string[]) => {
    const bounds = startEndLogIdsFromClicked(sourceIds, dateById);
    if (!bounds.startLogId) return;
    const next = nextAuditsStartEndLogIds(
      which,
      {
        startLogId: bounds.startLogId,
        endLogId: bounds.endLogId || bounds.startLogId,
      },
      { startLogId, endLogId },
      dateById,
    );
    applyIdsAndTime({
      startLogId: next.startLogId || null,
      endLogId: next.endLogId || null,
    });
  };

  const clearLogIdRangeBound = (which: 'start' | 'end') => {
    applyIdsAndTime(clearAuditsStartEndLogIdBound(which, { startLogId, endLogId }));
  };

  const clearLogIdRange = () => {
    dispatch(setAuditsStartEndLogIds(null));
  };

  return {
    startLogId,
    endLogId,
    dateById,
    applyLogIdRangeBound,
    clearLogIdRangeBound,
    clearLogIdRange,
  };
};
